export const SESSION_TIMEOUT_MS = 5 * 60 * 1000;
export const SESSION_EXPIRY_EVENT = 'aiscoreboard-session-expiry-changed';
export const SEAT_TOKEN_KEYS = ['aiscoreboard.token.0', 'aiscoreboard.token.1'] as const;

const expiryKey = (key: string): string => `${key}.expiry`;

export function sessionDeadline(key: string, token: string): number | null {
  const raw = window.localStorage.getItem(expiryKey(key));
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null || !('token' in value)
      || !('deadline' in value) || typeof value.token !== 'string'
      || typeof value.deadline !== 'number' || !Number.isFinite(value.deadline)) {
      throw new Error('Invalid session deadline');
    }
    return value.token === token ? value.deadline : null;
  } catch (error) {
    console.warn('[session-timeout] Invalid expiry; requiring a fresh login', error);
    return 0;
  }
}

export function validSessionToken(key: string, now = Date.now()): string | null {
  if (typeof window === 'undefined') return null;
  const token = window.localStorage.getItem(key);
  if (!token) return null;
  const deadline = sessionDeadline(key, token);
  return deadline !== null && deadline <= now ? null : token;
}

export function setSessionDeadline(key: string, token: string, deadline: number): void {
  if (window.localStorage.getItem(key) !== token || sessionDeadline(key, token) === deadline) return;
  window.localStorage.setItem(expiryKey(key), JSON.stringify({ token, deadline }));
  window.dispatchEvent(new Event(SESSION_EXPIRY_EVENT));
}

export function clearSessionDeadline(key: string): void {
  if (window.localStorage.getItem(expiryKey(key)) === null) return;
  window.localStorage.removeItem(expiryKey(key));
  window.dispatchEvent(new Event(SESSION_EXPIRY_EVENT));
}

export function watchSessionExpiry(check: () => number | null, extraEvents: string[] = []): () => void {
  let timer: number | undefined;
  let checking = false;
  const run = (): void => {
    if (checking) return;
    window.clearTimeout(timer);
    checking = true;
    let deadline: number | null;
    try {
      deadline = check();
    } finally {
      checking = false;
    }
    if (deadline !== null) timer = window.setTimeout(run, Math.max(0, deadline - Date.now()));
  };
  const events = ['focus', 'pageshow', 'storage', SESSION_EXPIRY_EVENT, ...extraEvents];
  for (const event of events) window.addEventListener(event, run);
  document.addEventListener('visibilitychange', run);
  run();
  return () => {
    window.clearTimeout(timer);
    for (const event of events) window.removeEventListener(event, run);
    document.removeEventListener('visibilitychange', run);
  };
}
